import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, Link2, Mail, Facebook, Twitter } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "./Button";
import Alert from "./Alert";

/**
 * ShareModal
 * - Mobile: możesz nadal preferować navigator.share() przed otwarciem modala (robimy to w ProjectDetailsPage)
 * - Desktop: modal z kopiowaniem + szybkie linki
 */
function ShareModal({ open, onClose, url, title, text, copied, onCopy }) {
  const { t } = useTranslation("projectDetails");

  // Zamknięcie ESC
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Lock scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const encodedUrl = useMemo(() => encodeURIComponent(url || ""), [url]);
  const encodedText = useMemo(
    () => encodeURIComponent(text || title || ""),
    [text, title],
  );
  const encodedTitle = useMemo(() => encodeURIComponent(title || ""), [title]);

  const shareLinks = useMemo(() => {
    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      x: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      email: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
    };
  }, [encodedUrl, encodedText, encodedTitle]);

  if (!open) return null;

  return createPortal(
    <>
      {/* opcjonalny toast (jeśli chcesz mieć info “skopiowano”) */}
      {copied && (
        <Alert variant="success" onClose={() => {}} autoClose={1500}>
          {t("share.copied")}
        </Alert>
      )}

      <div className="share-modal" role="dialog" aria-modal="true">
        <button
          className="share-modal__backdrop"
          onClick={onClose}
          aria-label={t("share.aria.close")}
        />

        <div className="share-modal__panel">
          <div className="share-modal__header">
            <div className="share-modal__title-wrap">
              <div className="share-modal__kicker">{t("share.kicker")}</div>
              <h3 className="share-modal__title">{t("share.title")}</h3>
            </div>

            <button className="share-modal__close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>

          <div className="share-modal__body">
            <div className="share-modal__section">
              <div className="share-modal__label">{t("share.linkLabel")}</div>

              <div className="share-modal__link-row">
                <div className="share-modal__link" title={url}>
                  <Link2 size={16} />
                  <span>{url}</span>
                </div>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onCopy}
                  icon={<Link2 size={16} />}
                >
                  {t("share.copy")}
                </Button>
              </div>

              <div className="share-modal__hint">{t("share.hint")}</div>
            </div>

            <div className="share-modal__section">
              <div className="share-modal__label">{t("share.quick")}</div>

              <div className="share-modal__buttons">
                <a
                  className="share-modal__social"
                  href={shareLinks.facebook}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Facebook size={18} />
                  <span>{t("share.platforms.facebook")}</span>
                </a>

                <a
                  className="share-modal__social"
                  href={shareLinks.x}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Twitter size={18} />
                  <span>{t("share.platforms.x")}</span>
                </a>

                <a className="share-modal__social" href={shareLinks.email}>
                  <Mail size={18} />
                  <span>{t("share.platforms.email")}</span>
                </a>
              </div>
            </div>
          </div>

          <div className="share-modal__footer">
            <Button variant="secondary" size="md" onClick={onClose}>
              {t("share.actions.close")}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default ShareModal;
